import { useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import {
  type BrandInsightsInstanceContext,
  type BrandInsightsPageContext,
  ChatContextType,
  getChatContextMetadata,
  getSegmentFromChatContext,
} from '@trinity/chat';
import {
  ChatSessionInputContext,
  ChatSessionQueriesContext,
  ActiveChatSessionContext,
  ChatPageContextDataContext,
  ChatSessionSelectedQueryContext,
  ChatSessionFiltersContext,
  ChatSessionPendingQueryContext,
  ChatSessionPendingHintPromptIdentifierContext,
  ChatSettingsContext,
  ChatSessionMessageQueriesContext,
  ChatContextItemsContext,
  ChatLandingCardsVisibleContext,
  ChatSessionPendingAttachmentsContext,
} from '@trinity/chat-components/components/Chat/context';
import { SegmentChatContext } from '@trinity/chat-components/components/BrandInsights/SegmentChat.Context';
import { AgentResourcesProvider } from '@trinity/chat-components/components/Chat.Header/AgentResources.Container';
import {
  ChatHistoryItemsContext,
  ChatHistoryManagerContext,
  ChatHistorySharedContext,
  ChatTabsContext,
} from '@trinity/chat-components/components/Chat.History/context';
import { useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import { ChatAgentsContainer } from '@trinity/chat-components/containers/ChatContexts/Chat.Agents.Container';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { buildMessageQueries } from '@trinity/chat-components/utils';
import { ChatStateDispatchContext } from './context';
import { chatReducer, generateEmptyChatState } from './state';
import { useActivePageSync } from './useActivePageSync';
import { useChatSettings } from './useChatSettings';
import { useSegmentTagFilterSync } from './useSegmentTagFilterSync';

type Props = {
  children: React.ReactNode;
};

function resolveContextEntityId(context: BrandInsightsPageContext.Item): string | number {
  switch (context.type) {
    case ChatContextType.Project:
      return context.data.projectId;
    case ChatContextType.Transcript:
      return context.data.transcriptId;
    case ChatContextType.Document:
      return context.data.documentId;
    case ChatContextType.Folder:
      return context.data.folderId;
    case ChatContextType.Brand:
      return context.data.brandId;
    case ChatContextType.KolProfile:
      return context.data.id ?? '';
    case ChatContextType.Agent:
      // subject.id is the actual entity (e.g. projectId); identifier is the agent type constant
      return context.data.subject?.id ?? context.data.identifier;
    default:
      return '';
  }
}

function resolveContextKey(context: BrandInsightsPageContext.Item | undefined, brandId: string): string {
  if (!context) return `none:${brandId}`;
  return `${context.type}:${resolveContextEntityId(context)}:${brandId}`;
}

/**
 * Page-sync key: the base key plus the segment / patient-journey the page is showing. Navigating
 * between two segments (or journeys) of the same project keeps the base key, but must still refresh
 * the chat context metadata and drop the previous segment's state.
 */
function resolvePageSyncKey(context: BrandInsightsInstanceContext.ItemWithMetadata,brandId: string): string {
  const segmentId = getSegmentFromChatContext(context)?.id ?? '';
  const journeyId = getChatContextMetadata(context)?.patientJourneyId ?? '';
  return `${resolveContextKey(context, brandId)}:segment=${segmentId}:journey=${String(journeyId)}`;
}

const ChatStateContainer = ({ children }: Props) => {
  const features = useGroupFeatures();
  const settings = useChatSettings();
  const brandIdentifier = useActiveBrandIdentifier();
  const contextItems = useContext(ChatContextItemsContext);
  const pageContext = useContext(ChatPageContextDataContext);

  if (!brandIdentifier) {
    throw new Error('No active brand identifier found for ChatStateContainer');
  }

  const [state, dispatch] = useReducer(
    chatReducer,
    generateEmptyChatState({
      context: pageContext,
      contextItems,
      features,
      settings,
      brandIdentifier,
      landingCardsVisible: true,
    })
  );

  const memoizedContext = useMemo(() => pageContext, [pageContext]);
  useActivePageSync(state.session.chatInstance);
  useSegmentTagFilterSync({
    context: state.session.chatInstance.context,
    chatIdentifier: state.session.chatInstance.identifier,
    tags: state.session.filters.tags,
    dispatch,
  });

  // "Chat with Segment" is scoped to the chat it started in. Once the chat's context changes (e.g. the
  // user switches to a Brand/Folder/Document), a leftover flag would keep Tags and Reset locked.
  const { segmentChatStatus, setSegmentChatStatus, setSegmentIconUrl } = useContext(SegmentChatContext);
  const chatContextKey = resolveContextKey(state.session.chatInstance.context, brandIdentifier);
  const prevChatContextKeyRef = useRef(chatContextKey);
  useEffect(() => {
    const changed = prevChatContextKeyRef.current !== chatContextKey;
    prevChatContextKeyRef.current = chatContextKey;
    if (!changed || !segmentChatStatus) return;

    setSegmentChatStatus(false);
    setSegmentIconUrl(undefined);
  }, [chatContextKey, segmentChatStatus, setSegmentChatStatus, setSegmentIconUrl]);

  const contextKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!memoizedContext) return;
    const nextKey = resolvePageSyncKey(memoizedContext, brandIdentifier);
    if (contextKeyRef.current === nextKey) return;
    const isFirstSync = contextKeyRef.current === null;
    contextKeyRef.current = nextKey;

    // A different segment/page invalidates the previous "Chat with Segment" state, which would
    // otherwise re-add the old segment on submit next to the newly synced tag.
    if (!isFirstSync) {
      setSegmentChatStatus(false);
      setSegmentIconUrl(undefined);
    }
    dispatch({
      type: 'reset-chat-state',
      payload: {
        context: memoizedContext,
        brandIdentifier,
      },
    });
  }, [memoizedContext, dispatch, brandIdentifier, setSegmentChatStatus, setSegmentIconUrl]);

  const selectedQuery = useMemo(() => {
    return state.session.queries[state.session.selectedQueryIdentifier];
  }, [state.session.queries, state.session.selectedQueryIdentifier]);

  const queries = useMemo(
    () =>
      buildMessageQueries({
        selectedQueryIdentifier: selectedQuery?.identifier,
        queries: state.session.queries,
      }),
    [selectedQuery?.identifier, state.session.queries]
  );

  const tabsValue = useMemo(() => {
    const allChats = [...state.history.items, ...state.history.shared];
    const chatMap = new Map(allChats.map((chat) => [chat.identifier, chat]));

    const sessionInstance = state.session.chatInstance;

    const resolvedItems = state.history.tabs.items
      .map((identifier) => {
        const chat = chatMap.get(identifier);
        if (chat) {
          return {
            id: chat.id,
            identifier: chat.identifier,
            name: chat.name ? chat.name : 'New Chat',
          };
        }

        if (sessionInstance.identifier === identifier) {
          return {
            id: sessionInstance.id,
            identifier: sessionInstance.identifier,
            name: sessionInstance.name ? sessionInstance.name : 'New Chat',
          };
        }

        const snapshot = state.unsavedTabSnapshots[identifier];
        if (snapshot) {
          return {
            id: snapshot.chatInstance.id,
            identifier: snapshot.chatInstance.identifier,
            name: snapshot.chatInstance.name ? snapshot.chatInstance.name : 'New Chat',
          };
        }

        return null;
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);

    return {
      index: state.history.tabs.index,
      items: resolvedItems,
    };
  }, [
    state.history.tabs.index,
    state.history.tabs.items,
    state.history.items,
    state.history.shared,
    state.session.chatInstance,
    state.unsavedTabSnapshots,
  ]);

  return (
    <ChatStateDispatchContext.Provider value={dispatch}>
      <ChatSessionPendingQueryContext.Provider value={state.session.pendingQuery}>
        <ChatSessionPendingHintPromptIdentifierContext.Provider
          value={state.session.pendingHintPromptIdentifier}
        >
          <ChatSessionQueriesContext.Provider value={state.session.queries}>
            <ChatSessionInputContext.Provider value={state.session.input}>
              <ChatSessionFiltersContext.Provider value={state.session.filters}>
                <ActiveChatSessionContext.Provider value={state.session.chatInstance}>
                  <ChatSessionSelectedQueryContext.Provider value={selectedQuery}>
                    <ChatHistoryItemsContext.Provider value={state.history.items}>
                      <ChatHistorySharedContext.Provider value={state.history.shared}>
                        <ChatHistoryManagerContext.Provider value={state.history.manager}>
                          <ChatTabsContext.Provider value={tabsValue}>
                            <ChatSettingsContext.Provider value={settings}>
                              <ChatSessionMessageQueriesContext.Provider value={queries}>
                                <ChatLandingCardsVisibleContext.Provider
                                  value={state.session.landingCardsVisible}
                                >
                                  <ChatSessionPendingAttachmentsContext.Provider
                                    value={state.session.pendingAttachments}
                                  >
                                    <AgentResourcesProvider>{children}</AgentResourcesProvider>
                                  </ChatSessionPendingAttachmentsContext.Provider>
                                </ChatLandingCardsVisibleContext.Provider>
                              </ChatSessionMessageQueriesContext.Provider>
                            </ChatSettingsContext.Provider>
                          </ChatTabsContext.Provider>
                        </ChatHistoryManagerContext.Provider>
                      </ChatHistorySharedContext.Provider>
                    </ChatHistoryItemsContext.Provider>
                  </ChatSessionSelectedQueryContext.Provider>
                </ActiveChatSessionContext.Provider>
              </ChatSessionFiltersContext.Provider>
            </ChatSessionInputContext.Provider>
          </ChatSessionQueriesContext.Provider>
        </ChatSessionPendingHintPromptIdentifierContext.Provider>
      </ChatSessionPendingQueryContext.Provider>
    </ChatStateDispatchContext.Provider>
  );
};

type StateWithContextProps = Props & {
  emptyFallback?: React.ReactNode;
};

const StateWithContext = (props: StateWithContextProps) => {
  return (
    <ChatAgentsContainer emptyFallback={props.emptyFallback}>
      <ChatStateContainer>{props.children}</ChatStateContainer>
    </ChatAgentsContainer>
  );
};

export { StateWithContext as ChatStateContainer };
